import { API_URLS } from "../Constants/api.constants";
import api from "./api";
import { errorHandling } from "./error.handling";

const basePath = `${API_URLS.CUSTOMERS}/customers`;

async function search(payload) {
  try {
    const params = new URLSearchParams();
    if (payload?.ClientName) params.append("ClientName", payload.ClientName);
    if (payload?.Doc) params.append("Doc", payload.Doc);

    let response = await api.get(`${basePath}/lookup?${params.toString()}`);
    let treated = errorHandling.handleResponse(response, "clientService.search");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.search");
    return null;
  }
}

async function fetchClient(id) {
  try {
    let response = await api.get(`${basePath}/${id}`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchClient");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchClient");
    return null;
  }
}

// Payload de POST/PUT ainda não confirmado com o backend — usei os nomes de
// campo do GET /customers/{id} (PersonType, SimpleOption, CreditAnalysis,
// Dda, PaymentBook, ServicePlanId, AdministrativeRestrictionId,
// RepresentativeId, SalespersonId). Ajustar se o schema esperado for diferente.
async function createClient(payload) {
  try {
    let response = await api.post(basePath, JSON.stringify(payload));
    let treated = errorHandling.handleResponse(response, "clientService.createClient");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.createClient");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
      raw: error?.response?.data,
    };
  }
}

async function updateClient(id, payload) {
  try {
    let response = await api.put(`${basePath}/${id}`, JSON.stringify(payload));
    let treated = errorHandling.handleResponse(response, "clientService.updateClient");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.updateClient");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
      raw: error?.response?.data,
    };
  }
}

async function fetchPlans() {
  try {
    let response = await api.get(`${basePath}/plans`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchPlans");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchPlans");
    return null;
  }
}

async function fetchPayDays() {
  try {
    let response = await api.get(`${basePath}/plans/pay-days`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchPayDays");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchPayDays");
    return null;
  }
}

async function fetchPaymentMethods() {
  try {
    let response = await api.get(`${basePath}/plans/payment-methods`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchPaymentMethods");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchPaymentMethods");
    return null;
  }
}

async function fetchInstallmentPlans() {
  try {
    let response = await api.get(`${basePath}/plans/installment-plans`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchInstallmentPlans");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchInstallmentPlans");
    return null;
  }
}

async function fetchContractDurations() {
  try {
    let response = await api.get(`${basePath}/plans/contract-durations`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchContractDurations");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchContractDurations");
    return null;
  }
}

async function fetchContractTypes() {
  try {
    let response = await api.get(`${basePath}/plans/contract-types`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchContractTypes");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchContractTypes");
    return null;
  }
}

async function fetchCommercialTerms(commercialConditionId) {
  try {
    let response = await api.get(
      `${basePath}/plans/commercial-conditions/${commercialConditionId}/commercial-terms`
    );
    let treated = errorHandling.handleResponse(response, "clientService.fetchCommercialTerms");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchCommercialTerms");
    return null;
  }
}

async function fetchContractedFeatures(commercialConditionId) {
  try {
    let response = await api.get(
      `${basePath}/plans/commercial-conditions/${commercialConditionId}/contracted-features`
    );
    let treated = errorHandling.handleResponse(response, "clientService.fetchContractedFeatures");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchContractedFeatures");
    return null;
  }
}

// Issue #504 — lista os contratos ativos do cliente do usuário autenticado.
// Endpoint não recebe parâmetro de filtro: traz tudo de uma vez e o filtro
// (pagante/status) é aplicado no front, em cima do que já foi carregado.
async function fetchActiveContracts() {
  try {
    let response = await api.get(`${basePath}/contracts/active`);
    let treated = errorHandling.handleResponse(response, "clientService.fetchActiveContracts");
    if (treated) return treated;
    return null;
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchActiveContracts");
    return null;
  }
}

async function fetchRegionals() {
  try {
    let response = await api.get(`${basePath}/regionals`);

    let treated = errorHandling.handleResponse(
      response,
      "clientService.fetchRegionals"
    );
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "clientService.fetchRegionals");
    return [];
  }
}

export const clientService = {
  search,
  fetchClient,
  createClient,
  updateClient,
  fetchPlans,
  fetchPayDays,
  fetchPaymentMethods,
  fetchInstallmentPlans,
  fetchContractDurations,
  fetchContractTypes,
  fetchCommercialTerms,
  fetchContractedFeatures,
  fetchActiveContracts,
  fetchRegionals,
};
